import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '../../shared/components/ui/Button/Button'
import { Input } from '../../shared/components/ui/Input/Input'
import { useToast } from '../../shared/context/ToastContext'
import { useApiError } from '../../shared/hooks/useApiError'
import authService from '../LoginPage/services/authService'
import { validatePassword, getPasswordRequirements } from '../../shared/utils/passwordValidation'
import { useTheme } from '../../shared/context/ThemeContext'
import { useBrand } from '../../shared/context/BrandContext'
import './styles.css'

export default function ResetPasswordPage() {
  const { t } = useTranslation()
  const { effectiveTheme } = useTheme()
  const { brand, getLogo } = useBrand()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showRequirements, setShowRequirements] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { showSuccess, showError, showWarning } = useToast()
  const { handleError } = useApiError({ notificationStrategy: { showError, showWarning } })
  
  const passwordRequirements = getPasswordRequirements(password)
  
  const token = searchParams.get('token')

  useEffect(() => {
    if (!token) {
      showError(t('auth.invalidToken'))
      navigate('/login')
    }
  }, [token, navigate, showError, t])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!password || !confirmPassword) {
      showError(t('auth.completeAllFields'))
      return
    }

    if (password !== confirmPassword) {
      showError(t('auth.passwordsDoNotMatch'))
      return
    }

    // Validar fortaleza de la contraseña
    const validation = validatePassword(password)
    if (!validation.isValid) {
      showError(validation.errors[0])
      return
    }

    if (!token) {
      showError(t('auth.invalidToken'))
      return
    }

    setIsLoading(true)

    try {
      const response = await authService.resetPassword(token, password)
      showSuccess(response.message || t('auth.passwordResetSuccess'))
      
      // Redirigir al login después de 2 segundos
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    } catch (error) {
      handleError(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="reset-password-page">
      <div className="reset-password-card">
        <div className="reset-password-logo-container">
          <img src={getLogo('iso', 'light')} alt={brand.displayName} className="reset-password-logo" />
        </div>
        <h1>{t('auth.resetPasswordTitle')}</h1>
        <p className="description">
          {t('auth.resetPasswordDescription')}
        </p>
        
        <form onSubmit={handleSubmit} className="reset-password-form">
          <Input
            type="password"
            placeholder={t('auth.newPassword')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setShowRequirements(true)}
            disabled={isLoading}
          />
          
          {showRequirements && password && (
            <div className="password-requirements">
              <p className="requirements-title">La contraseña debe contener:</p>
              <ul className="requirements-list">
                <li className={passwordRequirements.minLength ? 'valid' : 'invalid'}>
                  {passwordRequirements.minLength ? '✓' : '○'} Mínimo 8 caracteres
                </li>
                <li className={passwordRequirements.hasUpperCase ? 'valid' : 'invalid'}>
                  {passwordRequirements.hasUpperCase ? '✓' : '○'} Una letra mayúscula
                </li>
                <li className={passwordRequirements.hasLowerCase ? 'valid' : 'invalid'}>
                  {passwordRequirements.hasLowerCase ? '✓' : '○'} Una letra minúscula
                </li>
                <li className={passwordRequirements.hasNumber ? 'valid' : 'invalid'}>
                  {passwordRequirements.hasNumber ? '✓' : '○'} Un número
                </li>
                <li className={passwordRequirements.hasSpecialChar ? 'valid' : 'invalid'}>
                  {passwordRequirements.hasSpecialChar ? '✓' : '○'} Un carácter especial (!@#$%^&*...)
                </li>
              </ul>
            </div>
          )}
          
          <Input
            type="password"
            placeholder={t('auth.confirmPassword')}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isLoading}
          />
          
          <Button type="submit" disabled={isLoading}>
            {isLoading ? t('auth.resetting') : t('auth.resetPasswordButton')}
          </Button>
          
          <Link to="/login" className="back-link">
            {t('auth.backToLogin')}
          </Link>
        </form>
      </div>
    </div>
  )
}
